import PropTypes from 'prop-types';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import { clamp } from 'lodash';
import Profile from '../../../formBarbers/barberForm/containers/Profile';
import BrandServices from '../../../formBarbers/barberForm/components/BrandServices';
import {
  canUserModifyOptions,
  canUserModifyProfile,
  canUserViewProfile,
  canUserViewSchedules,
} from '../../../../../common/helpers/users';
import Notifications from '../../../formBarbers/barberForm/components/Notifications';
import Schedule from '../../../formBarbers/barberForm/containers/BrandSchedule';
import DaysOffOn from '../../../formBarbers/barberForm/containers/BrandDaysOffOn';
import Permissions from '../../../formBarbers/barberForm/components/Permissions';
import Options from '../../../formBarbers/barberForm/containers/BrandOptions';
import Locations from '../../../formBarbers/barberForm/LocationsTab';
import { isEditMode } from '../helpers/isEditMode';
import withProfileHoc from './tabsHoc/withProfileHoc';
import withAllLocationsTab from './tabsHoc/withAllLocationsTab';
import withMinInterval from './withMinInterval';
import { BarberPayments } from '../../../../../../modules/shared/barbers/barber-form/barber-payments';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../../../../modules/infra/feature-flags';

export const TAB_NAMES = {
  PROFILE: 'Profile',
  LOCATIONS: 'Locations',
  SERVICES: 'Services',
  NOTIFICATIONS: 'Notifications',
  SCHEDULE: 'Schedule',
  DAYS_OFF_ON: 'Days Off/On',
  PERMISSIONS: 'Permissions',
  OPTIONS: 'Options',
  PAYMENTS: 'Payments',
};

const TABS = [
  {
    title: TAB_NAMES.PROFILE,
    Component: withProfileHoc(Profile),
  },
  {
    title: TAB_NAMES.PAYMENTS,
    Component: withAllLocationsTab(BarberPayments),
    showLocationSelector: false,
    shouldHide: (props) => !props[flagNames.HYBRID_STAFF_PAYMENTS_TAB],
    getComponentProps: (props) => ({
      formSectionName: props.selectedLocation,
    }),
  },
  {
    title: TAB_NAMES.LOCATIONS,
    Component: Locations,
  },
  {
    title: TAB_NAMES.SERVICES,
    Component: BrandServices,
    useFormSection: false,
    shouldHide: (props) => !canUserViewProfile(props.permissions),
  },
  {
    title: TAB_NAMES.NOTIFICATIONS,
    Component: withMinInterval(Notifications),
    showLocationSelector: true,
    getComponentProps: props => ({
      formSectionName: props.selectedLocation,
      edit: isEditMode(props),
    }),
  },
  {
    title: TAB_NAMES.SCHEDULE,
    Component: Schedule,
    showLocationSelector: true,
    shouldHide: (props) => !canUserViewSchedules(props.permissions),
    getComponentProps: props => ({
      formSectionName: props.selectedLocation,
    }),
  },
  {
    title: TAB_NAMES.DAYS_OFF_ON,
    Component: DaysOffOn,
    showLocationSelector: true,
    shouldHide: (props) => !canUserViewSchedules(props.permissions),
    getComponentProps: props => ({
      formSectionName: props.selectedLocation,
    }),
  },
  {
    title: TAB_NAMES.PERMISSIONS,
    Component: Permissions,
    showLocationSelector: true,
    shouldHide: props => !canUserViewProfile(props.permissions),
    getComponentProps: props => ({
      brandBarberSelectedShopId: props.selectedLocation,
      brandBarberShops: props.barberShops,
    }),
  },
  {
    title: TAB_NAMES.OPTIONS,
    Component: Options,
    showLocationSelector: true,
    shouldHide: (props) =>
      !canUserViewProfile(props.permissions) ||
      !canUserModifyOptions(props.user) ||
      !canUserModifyProfile(props.permissions),
  },
];

export const tabShape = PropTypes.shape({
  title: PropTypes.string.isRequired,
  Component: PropTypes.elementType.isRequired,
  showLocationSelector: PropTypes.bool,
  getComponentProps: PropTypes.func,
  getTabProps: PropTypes.func,
});

const withTabs = compose(
  withInjectFeatureFlagsToProps([flagNames.HYBRID_STAFF_PAYMENTS_TAB]),
  withProps((props) => ({
    tabs: TABS.filter((tab) => !tab.shouldHide?.(props)),
  })),
  withProps((props) => ({
    lastTabIndex: props.tabs.length - 1,
  })),
  withStateHandlers(
    { activeTab: 0 },
    {
      setActiveTab: () => (activeTab) => ({ activeTab }),
      nextTab:
        ({ activeTab }, { lastTabIndex }) =>
        () => ({
          activeTab: clamp(activeTab + 1, 0, lastTabIndex),
        }),
      previousTab:
        ({ activeTab }, { lastTabIndex }) =>
        () => ({
          activeTab: clamp(activeTab - 1, 0, lastTabIndex),
        }),
    }
  ),
  withProps((props) => ({
    isServicesTab: props.activeTab === TABS.findIndex(({ title }) => title === TAB_NAMES.SERVICES),
  })),
  withHandlers({
    isTabDisabled: (props) => (tabIndex) => !isEditMode(props) && props.activeTab < tabIndex,
  })
);

export const tabsPropsTypes = {
  activeTab: PropTypes.number.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  nextTab: PropTypes.func.isRequired,
  previousTab: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(tabShape),
};

export default withTabs;
